import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useReveal from "@/hooks/useReveal";
import { submitContact } from "@/lib/api";

export default function ContactPage() {
  const r1 = useReveal();
  const r2 = useReveal();

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      await submitContact(form);
      setSent(true);
      toast.success("Message received", {
        description: "We'll be in touch shortly.",
      });
    } catch (err) {
      toast.error("Could not send message", {
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#0a0a0a] text-white">
      <Header />
      <main className="pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {/* Breadcrumb */}
          <div className="font-label text-[#8a8a85] mb-12">
            <Link
              to="/"
              data-testid="contact-breadcrumb-home"
              className="link-underline"
            >
              Home
            </Link>
            <span className="mx-3">/</span>
            <span className="text-[#d4af37]">Contact</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
            {/* Heading + studio info */}
            <div
              ref={r1.ref}
              className={`reveal ${r1.inView ? "in-view" : ""} lg:col-span-6`}
            >
              <span className="font-label text-[#d4af37]">— The Studio —</span>
              <h1 className="mt-5 font-display text-[clamp(2.6rem,6vw,5.4rem)] leading-[0.98] tracking-[-0.025em]">
                Get in{" "}
                <span className="font-editorial italic gold-text-gradient">
                  touch.
                </span>
              </h1>
              <p className="mt-7 max-w-md text-[#cfcfca] text-lg leading-relaxed font-light">
                Press. Partnerships. Preorder. Wholesale.
              </p>

              <div className="mt-14 space-y-10 max-w-md">
                <div>
                  <span className="font-label text-[#8a8a85]">Email</span>
                  <p className="mt-2 font-display text-2xl">
                    <a
                      href="mailto:Afrqa@afrqa.com"
                      data-testid="contact-email-link"
                      className="link-underline text-white"
                    >
                      Afrqa@afrqa.com
                    </a>
                  </p>
                </div>
                <div>
                  <span className="font-label text-[#8a8a85]">Studio</span>
                  <p
                    className="mt-2 font-display text-2xl text-white"
                    data-testid="contact-location"
                  >
                    Los Angeles, California
                  </p>
                </div>
                <div>
                  <span className="font-label text-[#8a8a85]">Instagram</span>
                  <p className="mt-2 font-display text-2xl">
                    <a
                      href="https://instagram.com/shopafrqa"
                      target="_blank"
                      rel="noreferrer"
                      data-testid="contact-instagram-link"
                      className="link-underline text-white"
                    >
                      @shopafrqa
                    </a>
                  </p>
                </div>
              </div>

              <div className="mt-16 border-t border-[#1f1f1f] pt-8 max-w-md">
                <p className="font-editorial italic text-[#cfcfca] text-xl">
                  Designed for{" "}
                  <span className="gold-text-gradient not-italic font-display">
                    the culture.
                  </span>
                </p>
              </div>
            </div>

            {/* Form */}
            <div
              ref={r2.ref}
              className={`reveal reveal-delay-1 ${
                r2.inView ? "in-view" : ""
              } lg:col-span-6`}
            >
              <div className="relative border border-[#1f1f1f] bg-[#0c0c0c] p-8 md:p-12">
                {/* Gold corner marks */}
                <span
                  aria-hidden
                  className="absolute -top-px -left-px w-6 h-6 border-l border-t border-[#d4af37]"
                />
                <span
                  aria-hidden
                  className="absolute -top-px -right-px w-6 h-6 border-r border-t border-[#d4af37]"
                />
                <span
                  aria-hidden
                  className="absolute -bottom-px -left-px w-6 h-6 border-l border-b border-[#d4af37]"
                />
                <span
                  aria-hidden
                  className="absolute -bottom-px -right-px w-6 h-6 border-r border-b border-[#d4af37]"
                />

                {!sent ? (
                  <form
                    onSubmit={onSubmit}
                    data-testid="contact-form"
                    className="space-y-10"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-label text-[#d4af37]">
                        — Write to us —
                      </span>
                      <span className="font-editorial italic text-[#9a9a93] text-lg">
                        01 / 03
                      </span>
                    </div>

                    <div>
                      <label
                        htmlFor="contact-name"
                        className="font-label text-[#8a8a85] block mb-3"
                      >
                        Name
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        required
                        autoComplete="name"
                        value={form.name}
                        onChange={onChange("name")}
                        placeholder="Your name"
                        data-testid="contact-name-input"
                        className="afrqa-input"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="contact-email"
                        className="font-label text-[#8a8a85] block mb-3"
                      >
                        Email
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        required
                        autoComplete="email"
                        value={form.email}
                        onChange={onChange("email")}
                        placeholder="you@studio.com"
                        data-testid="contact-email-input"
                        className="afrqa-input"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="contact-message"
                        className="font-label text-[#8a8a85] block mb-3"
                      >
                        Message
                      </label>
                      <textarea
                        id="contact-message"
                        required
                        rows={5}
                        value={form.message}
                        onChange={onChange("message")}
                        placeholder="Tell us a little about what you have in mind…"
                        data-testid="contact-message-input"
                        className="afrqa-input resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit-btn"
                      className="btn-gold w-full"
                    >
                      {loading ? "Sending…" : "Send Message"}
                      {!loading && <span aria-hidden>→</span>}
                    </button>

                    <p className="text-[#6a6a65] text-xs tracking-[0.18em] uppercase text-center">
                      Replies within 48 hours · Mon–Fri
                    </p>
                  </form>
                ) : (
                  <div
                    className="py-12 text-center"
                    data-testid="contact-success"
                  >
                    <span className="font-label text-[#d4af37]">
                      — Received —
                    </span>
                    <h2 className="mt-5 font-display text-3xl md:text-4xl leading-tight">
                      Thank you,{" "}
                      <span className="font-editorial italic gold-text-gradient">
                        {form.name.split(" ")[0] || "friend"}.
                      </span>
                    </h2>
                    <p className="mt-5 text-[#cfcfca] font-light max-w-sm mx-auto">
                      Your note has been received. We'll respond from{" "}
                      <span className="text-white">Afrqa@afrqa.com</span>.
                    </p>
                    <Link
                      to="/"
                      data-testid="contact-return-home"
                      className="btn-ghost-gold mt-10 inline-flex"
                    >
                      Return Home
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
